// src/pages/cadastros/Usuarios/UsuariosPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardsMobile from "../../../templates/mobile/CardsMobile";

import { LABEL_NIVEIS as NIVEL_LABEL, NIVEIS } from "../../../config/niveis";
import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";
import { pode, nivelUsuario } from "../../../utils/permissoes";
import { db } from "../../../config/firebase";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

function renderCodigo(u) {
  if (u.codigo) return u.codigo;
  if (u.codigoSequencial != null) {
    return `US${String(u.codigoSequencial).padStart(4, "0")}`;
  }
  if (u.id) return u.id.slice(0, 8);
  return "-";
}

// (export no final do arquivo)

function renderAcesso(u) {
  if (u.globalAccess || u.tipoAcesso === "GLOBAL") {
    return "Global (todas as empresas)";
  }
  if (u.empresasPermitidas && u.empresasPermitidas.length > 0) {
    return `Restrito (${u.empresasPermitidas.length} empresas)`;
  }
  if (u.empresa) return "Somente empresa base";
  return "-";
}

function UsuariosPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();

  // ✅ Corrige crash ao abrir "Cadastros → Usuários": variável "usuario" não existia aqui.
  const nivelEditor = nivelUsuario(usuarioLogado);
  const podeCriarUsuario = pode(usuarioLogado, "cadastros", "usuarios", "form");
  const podeEditarUsuarioBase = pode(usuarioLogado, "cadastros", "usuarios", "altera");

  function podeEditarUsuarioAlvo(u) {
    if (!podeEditarUsuarioBase) return false;
    const nivelAlvo = Number(u?.nivel ?? -1);
    if (nivelEditor === NIVEIS.GESTOR) return true;
    if (nivelEditor === NIVEIS.ADMIN) return nivelAlvo < NIVEIS.ADMIN;
    return false;
  }

  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const camposFiltro = useMemo(
    () => [
      { value: "nome", label: "Nome" },
      { value: "email", label: "E-mail" },
      { value: "telefone", label: "Telefone" },
      { value: "nivel", label: "Nível de acesso" },
      { value: "empresaAcesso", label: "Empresas com acesso" },
      { value: "codigo", label: "Código usuário" },
    ],
    []
  );

  async function handleExcluirUsuario(u) {
    try {
      if (!u?.id) return;

      const nome = u.nome || u.perfil?.nome || u.email || "este usuário";
      const ok = window.confirm(`Apagar ${nome}?`);
      if (!ok) return;

      // Evita apagar o próprio usuário logado
      const meuId = usuarioLogado?.uid || usuarioLogado?.id;
      if (meuId && u.id === meuId) {
        alert("Você não pode apagar seu próprio usuário.");
        return;
      }

      await deleteDoc(doc(db, "usuarios", u.id));
      setUsuarios((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) {
      console.error("Erro ao apagar usuário:", e);
      alert("Não foi possível apagar o usuário. Verifique sua permissão e tente novamente.");
    }
  }

  useEffect(() => {
    async function carregarUsuarios() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const usuariosRef = collection(db, "usuarios");

        let q;
        if (usuarioLogado.grupoId) {
          q = query(usuariosRef, where("grupoId", "==", usuarioLogado.grupoId));
        } else {
          q = query(usuariosRef, where("email", "==", usuarioLogado.email));
        }

        const snap = await getDocs(q);
        const lista = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setUsuarios(lista);
      } catch (e) {
        console.error("Erro ao carregar usuários:", e);
        setErro("Erro ao carregar usuários. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarUsuarios();
  }, [usuarioLogado]);

  const usuariosFiltrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return usuarios;

    return usuarios.filter((u) => {
      let valorBase = "";

      if (campoFiltro === "empresaAcesso") {
        if (u.globalAccess || u.tipoAcesso === "GLOBAL") {
          valorBase = "global";
        } else if (u.empresasPermitidas && u.empresasPermitidas.length > 0) {
          valorBase = u.empresasPermitidas.join(", ");
        } else if (u.empresa) {
          valorBase = u.empresa;
        }
      } else if (campoFiltro === "codigo") {
        valorBase = renderCodigo(u);
      } else {
        valorBase = u[campoFiltro] ?? "";
      }

      return String(valorBase).toLowerCase().includes(texto);
    });
  }, [usuarios, textoFiltro, campoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    usuariosFiltrados,
    "nome"
  );

  const usuariosParaCards = useMemo(
    () =>
      usuariosFiltrados.map((u) => ({
        id: u.id,
        nome: u.nome || "Sem nome",
        nivel: NIVEL_LABEL[u.nivel] ?? u.nivel ?? "Usuário",
        telefone: u.telefone || "-",
        email: u.email || "",
        status: u.ativo !== false,
      })),
    [usuariosFiltrados]
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Usuários"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        {carregando ? (
          <div className="m-loading">Carregando...</div>
        ) : erro ? (
          <div className="m-error">{erro}</div>
        ) : (
          <CardsMobile
            usuarios={usuariosParaCards}
            onHome={() => navigate("/")}
            onNovoUsuario={podeCriarUsuario ? () => navigate("/cadastros/usuarios/novo") : undefined}
            onEditarUsuario={(id) => navigate(`/cadastros/usuarios/editar/${id}`)}
            onVisualizarUsuario={(id) => navigate(`/cadastros/usuarios/editar/${id}`)}
            onExcluirUsuario={(id) => {
              const u = usuarios.find((x) => x.id === id);
              if (u) handleExcluirUsuario(u);
            }}
          />
        )}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Usuários" subtitulo="">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Usuários cadastrados</h3>
            <p>Lista de usuários do grupo/empresa, integrada ao Firebase.</p>
          </div>

          <div className="pc-table-header-actions">
            {podeCriarUsuario && (
              <Can modulo="cadastros" recurso="usuarios" acao="form">
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={() => navigate("/cadastros/usuarios/novo")}
                >
                  + Novo Usuário
                </button>
              </Can>
            )}
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={() => navigate("/cadastros/usuarios/novo")}
          placeholder="Filtrar usuários..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando usuários...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum usuário encontrado.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
  <thead>
    <tr>
      <th style={{ width: 44 }} />
      <SortableHeader
        label="Código"
        sortKey="codigoSequencial"
        onSort={requestSort}
        sortDirection={getSortDirection("codigoSequencial")}
      />
      <SortableHeader
        label="Nome"
        sortKey="nome"
        onSort={requestSort}
        sortDirection={getSortDirection("nome")}
      />
      <SortableHeader
        label="E-mail"
        sortKey="email"
        onSort={requestSort}
        sortDirection={getSortDirection("email")}
      />
      <SortableHeader
        label="Nível"
        sortKey="nivel"
        onSort={requestSort}
        sortDirection={getSortDirection("nivel")}
      />
      <th>Cor</th>
      <th>Status</th>
      <th style={{ textAlign: "right" }}>Ações</th>
    </tr>
  </thead>
  <tbody>
    {sortedData.map((u) => {
      const aberto = expandedId === u.id;

      return (
        <React.Fragment key={u.id}>
          <tr className={aberto ? "pc-row-open" : undefined}>
            <td>
              <button
                type="button"
                className="pc-expand-btn"
                aria-label={aberto ? "Recolher" : "Expandir"}
                onClick={() => setExpandedId((prev) => (prev === u.id ? null : u.id))}
              >
                <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>
                  ▸
                </span>
              </button>
            </td>
            <td>{renderCodigo(u)}</td>
            <td style={{ fontWeight: 600 }}>{u.nome || "-"}</td>
            <td>{u.email || "-"}</td>
            <td>{NIVEL_LABEL[u.nivel] ?? u.nivel ?? "-"}</td>
            <td>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: u.corAgenda || "#3b82f6",
                    border: "1px solid rgba(148, 163, 184, 0.45)",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontSize: 12, opacity: 0.8 }}>
                  {u.corAgenda || "#3b82f6"}
                </span>
              </div>
            </td>
            <td>
              <span className={u.ativo !== false ? "pc-badge pc-badge-ativo" : "pc-badge pc-badge-inativo"}>
                {u.ativo !== false ? "Ativo" : "Inativo"}
              </span>
            </td>
            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              <Can modulo="cadastros" recurso="usuarios" acao="altera">
<button
                type="button"
                className="pc-icon-btn"
                title="Editar usuário"
                disabled={!podeEditarUsuarioAlvo(u)}
                onClick={() =>
                  podeEditarUsuarioAlvo(u) && navigate(`/cadastros/usuarios/editar/${u.id}`)
                }
              >
                ✏️
              </button>
</Can>
              <Can modulo="cadastros" recurso="usuarios" acao="altera">
<button
                type="button"
                className="pc-icon-btn"
                title="Apagar usuário"
                onClick={() => handleExcluirUsuario(u)}
              >
                🗑️
              </button>
</Can>
            </td>
          </tr>

          {aberto && (
            <tr className="pc-expanded-row">
              <td colSpan={8}>
                <div className="pc-expand-panel">
                  <div className="pc-expand-grid">
                    <div className="pc-expand-item">
                      <div className="pc-expand-label">Telefone</div>
                      <div className="pc-expand-value">{u.telefone || "-"}</div>
                    </div>

                    <div className="pc-expand-item">
                      <div className="pc-expand-label">Acesso</div>
                      <div className="pc-expand-value">{renderAcesso(u)}</div>
                    </div>

                    <div className="pc-expand-item">
                      <div className="pc-expand-label">Código</div>
                      <div className="pc-expand-value">{renderCodigo(u)}</div>
                    </div>

                    <div className="pc-expand-item">
                      <div className="pc-expand-label">Cor calendário</div>
                      <div className="pc-expand-value">
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: u.corAgenda || "#3b82f6",
                              border: "1px solid rgba(148, 163, 184, 0.45)",
                              display: "inline-block",
                            }}
                          />
                          {u.corAgenda || "#3b82f6"}
                        </span>
                      </div>
                    </div>

                    <div className="pc-expand-item">
                      <div className="pc-expand-label">UID</div>
                      <div className="pc-expand-value">{u.id}</div>
                    </div>
                  </div>

                  <div className="pc-expand-actions">
                    <Can modulo="cadastros" recurso="usuarios" acao="altera">
                      <button
                        type="button"
                        className="pc-btn-secundario"
                        disabled={!podeEditarUsuarioAlvo(u)}
                        onClick={() =>
                          podeEditarUsuarioAlvo(u) && navigate(`/cadastros/usuarios/editar/${u.id}`)
                        }
                      >
                        Editar
                      </button>
                    </Can>

                    <Can modulo="cadastros" recurso="usuarios" acao="altera">
                      <button
                        type="button"
                        className="pc-btn-perigo"
                        disabled={!podeEditarUsuarioAlvo(u)}
                        onClick={() => podeEditarUsuarioAlvo(u) && handleExcluirUsuario(u)}
                      >
                        Excluir
                      </button>
                    </Can>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    })}
  </tbody>
</table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default UsuariosPage;
